import { z } from "zod";

export const ReportingPeriodSchema = z.object({
  id: z.string().uuid(),
  clientId: z.string().uuid(),
  year: z.number().int().min(2000).max(2100),
  month: z.number().int().min(1).max(12),
  label: z.string().min(1),
  uploadedAt: z.string().datetime({ offset: true }),
  sessionsTotal: z.number().int().nonnegative(),
  microSessionsExcluded: z.number().int().nonnegative(),
  skippedRows: z.number().int().nonnegative(),
});

export type ReportingPeriod = z.infer<typeof ReportingPeriodSchema>;

export const ReportingPeriodListResponseSchema = z.object({
  items: z.array(ReportingPeriodSchema),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
});

export type ReportingPeriodListResponse = z.infer<typeof ReportingPeriodListResponseSchema>;

// ── Dashboard data schemas (used by GET /periods/:id) ─────────────────────────

export const KPIsSchema = z.object({
  total_sessions: z.number(),
  total_hours: z.number(),
  productive_hours: z.number(),
  extended_session_rate: z.number(),
  active_stores: z.number(),
  total_stores: z.number(),
  network_adoption_pct: z.number(),
  fleet_idle_rate_pct: z.number(),
  micro_sessions_excluded: z.number(),
  // Historical periods' stored dashboardData blobs were computed before this
  // field existed and are served as-is (getById's cache-miss fallback reads
  // the frozen blob rather than recomputing full methodology), so it must
  // tolerate absence rather than fail strict parsing on every old period.
  total_active_users: z.number().default(0),
});

export type KPIs = z.infer<typeof KPIsSchema>;

export const StoreMetricsSchema = z.object({
  store: z.string(),
  sessions: z.number(),
  productive_sessions: z.number(),
  extended_sessions: z.number(),
  total_hours: z.number(),
  productive_hours: z.number(),
  devices_total: z.number(),
  devices_watch: z.number(),
  devices_bodyworn: z.number(),
  productive_hrs_per_device: z.number(),
  sessions_per_device: z.number(),
  avg_session_hours: z.number(),
  unique_users: z.number(),
  idle_devices: z.number(),
  fleet_idle_rate: z.number(),
  extended_rate: z.number(),
  tier: z.string().nullable(),
});

export type StoreMetrics = z.infer<typeof StoreMetricsSchema>;

export const PriorityTiersSchema = z.object({
  high: z.array(z.string()),
  watch_list: z.array(z.string()),
  on_track: z.array(z.string()),
  excellent: z.array(z.string()),
});

export type PriorityTiers = z.infer<typeof PriorityTiersSchema>;

export const UserMetricsSchema = z.object({
  user: z.string(),
  store: z.string(),
  sessions: z.number(),
  total_hours: z.number(),
  productive_hours: z.number(),
  extended_sessions: z.number(),
});

export type UserMetrics = z.infer<typeof UserMetricsSchema>;

export const DeviceTypeMetricsSchema = z.object({
  sessions: z.number(),
  total_hours: z.number(),
  productive_hours: z.number(),
  extended_rate: z.number(),
  avg_session_hours: z.number(),
  device_count: z.number(),
});

export type DeviceTypeMetrics = z.infer<typeof DeviceTypeMetricsSchema>;

export const StoreMixItemSchema = z.object({
  store: z.string(),
  total_devices: z.number(),
  watch_count: z.number(),
  bodyworn_count: z.number(),
  watch_pct: z.number(),
  bodyworn_pct: z.number(),
});

export type StoreMixItem = z.infer<typeof StoreMixItemSchema>;

export const DeviceTypeBreakdownSchema = z.object({
  Watch: DeviceTypeMetricsSchema,
  Bodyworn: DeviceTypeMetricsSchema,
  store_mix: z.array(StoreMixItemSchema),
});

export type DeviceTypeBreakdown = z.infer<typeof DeviceTypeBreakdownSchema>;

// A device the export flags Decommissioned=Yes -- either one that was never
// used (audit reference only, never counted in any fleet/idle/session
// metric), or one with real usage history whose export retroactively stamps
// the flag across all its rows once it's ever retired (confirmed on Coles);
// that usage stays counted, this only stops the roster carrying the device
// forward. deviceId defaults to "" for periods stored before this field
// existed.
export const DecommissionedDeviceSchema = z.object({
  store: z.string(),
  watchName: z.string(),
  deviceId: z.string().default(""),
});

export type DecommissionedDevice = z.infer<typeof DecommissionedDeviceSchema>;

export const MethodologyResultSchema = z.object({
  kpis: KPIsSchema,
  stores: z.array(StoreMetricsSchema),
  device_types: DeviceTypeBreakdownSchema,
  users: z.array(UserMetricsSchema),
  priority_tiers: PriorityTiersSchema,
  micro_sessions_excluded: z.number(),
  skipped_rows: z.number(),
  warnings: z.array(z.string()),
  decommissioned_devices: z.array(DecommissionedDeviceSchema),
});

export type MethodologyResult = z.infer<typeof MethodologyResultSchema>;

// ── Check-in activity schemas ────────────────────────────────────────────────

export const CheckinUserEntrySchema = z.object({
  user: z.string(),
  store: z.string(),
  checkins: z.number(),
  completed: z.number(),
  missed: z.number(),
  completion_rate: z.number(),
});

export type CheckinUserEntry = z.infer<typeof CheckinUserEntrySchema>;

export const CheckinStoreEntrySchema = z.object({
  store: z.string(),
  checkins: z.number(),
  completed: z.number().optional(),
  missed: z.number().optional(),
  completion_rate: z.number().optional(),
});

export type CheckinStoreEntry = z.infer<typeof CheckinStoreEntrySchema>;

export const CheckinTimelinePointSchema = z.object({
  date: z.string(),
  checkins: z.number(),
  completed: z.number(),
});

export type CheckinTimelinePoint = z.infer<typeof CheckinTimelinePointSchema>;

export const CheckinDataSchema = z.object({
  total_checkins: z.number(),
  overall_completion_rate: z.number().nullable(),
  by_store: z.array(CheckinStoreEntrySchema),
  by_user: z.array(CheckinUserEntrySchema),
  missed_by_user: z.array(CheckinUserEntrySchema),
  timeline: z.array(CheckinTimelinePointSchema),
  avg_duration_minutes: z.number().nullable(),
});

export type CheckinData = z.infer<typeof CheckinDataSchema>;

// ── Emergency activity schemas ────────────────────────────────────────────────

export const EmergencyStoreEntrySchema = z.object({
  store: z.string(),
  events: z.number(),
});

export type EmergencyStoreEntry = z.infer<typeof EmergencyStoreEntrySchema>;

export const EmergencyDeviceTypeEntrySchema = z.object({
  device_type: z.string(),
  events: z.number(),
});

export type EmergencyDeviceTypeEntry = z.infer<typeof EmergencyDeviceTypeEntrySchema>;

export const EmergencyStatusEntrySchema = z.object({
  status: z.string(),
  events: z.number(),
});

export type EmergencyStatusEntry = z.infer<typeof EmergencyStatusEntrySchema>;

export const EmergencyTriggerEntrySchema = z.object({
  trigger: z.string(),
  events: z.number(),
});

export type EmergencyTriggerEntry = z.infer<typeof EmergencyTriggerEntrySchema>;

export const EmergencyTimelinePointSchema = z.object({
  date: z.string(),
  events: z.number(),
});

export type EmergencyTimelinePoint = z.infer<typeof EmergencyTimelinePointSchema>;

// Per-team cross-tab of the same genuine/de-escalated split used for the
// genuine_events/deescalated_events KPIs below — `by_store` only has a
// single events total per team, which can't answer "which teams are seeing
// real incidents vs. stood-down ones".
export const EmergencyStoreStatusEntrySchema = z.object({
  store: z.string(),
  genuine: z.number(),
  deescalated: z.number(),
  other: z.number(),
  total: z.number(),
});

export type EmergencyStoreStatusEntry = z.infer<typeof EmergencyStoreStatusEntrySchema>;

export const EmergencyDataSchema = z.object({
  total_events: z.number(),
  testing_events: z.number(),
  // Confirmed-real and stood-down-after-review counts, split out of
  // total_events/by_status so callers don't each re-implement the status
  // keyword classification (see isGenuineEmergencyStatus/isDeescalatedStatus
  // in methodology.ts).
  //
  // Defaulted, not required: dashboard_data is computed once at upload time
  // and persisted as-is, never recomputed on read. Periods uploaded before
  // these fields existed have dashboard_data blobs that will never contain
  // them, so a bare z.number()/z.array() here would permanently fail
  // PeriodDetailSchema.parse() for every period stored before this change
  // shipped -- which is exactly what happened in production on 2026-09-07
  // (every tab but Daily Activity, which has its own endpoint, broke for
  // every client). See production-migration.md for the backfill that
  // recomputes real values for those periods from emergency_records.
  genuine_events: z.number().default(0),
  deescalated_events: z.number().default(0),
  by_store: z.array(EmergencyStoreEntrySchema),
  by_store_status: z.array(EmergencyStoreStatusEntrySchema).default([]),
  by_device_type: z.array(EmergencyDeviceTypeEntrySchema),
  by_status: z.array(EmergencyStatusEntrySchema),
  by_trigger: z.array(EmergencyTriggerEntrySchema),
  timeline: z.array(EmergencyTimelinePointSchema),
  notable: z.array(z.record(z.string(), z.string())),
});

export type EmergencyData = z.infer<typeof EmergencyDataSchema>;

// ── Team alert activity schemas ───────────────────────────────────────────────

export const TeamAlertStoreEntrySchema = z.object({
  store: z.string(),
  alerts: z.number(),
});

export type TeamAlertStoreEntry = z.infer<typeof TeamAlertStoreEntrySchema>;

export const TeamAlertTypeEntrySchema = z.object({
  alert_type: z.string(),
  alerts: z.number(),
});

export type TeamAlertTypeEntry = z.infer<typeof TeamAlertTypeEntrySchema>;

export const TeamAlertStatusEntrySchema = z.object({
  status: z.string(),
  alerts: z.number(),
});

export type TeamAlertStatusEntry = z.infer<typeof TeamAlertStatusEntrySchema>;

export const TeamAlertUserEntrySchema = z.object({
  user: z.string(),
  store: z.string(),
  alerts: z.number(),
});

export type TeamAlertUserEntry = z.infer<typeof TeamAlertUserEntrySchema>;

export const TeamAlertTimelinePointSchema = z.object({
  date: z.string(),
  alerts: z.number(),
});

export type TeamAlertTimelinePoint = z.infer<typeof TeamAlertTimelinePointSchema>;

export const TeamAlertDataSchema = z.object({
  total_alerts: z.number(),
  by_store: z.array(TeamAlertStoreEntrySchema),
  by_type: z.array(TeamAlertTypeEntrySchema),
  // Defaults to [] so periods stored before by_status existed still parse —
  // their dashboardData JSON has no by_status key.
  by_status: z.array(TeamAlertStatusEntrySchema).default([]),
  by_user: z.array(TeamAlertUserEntrySchema),
  timeline: z.array(TeamAlertTimelinePointSchema),
});

export type TeamAlertData = z.infer<typeof TeamAlertDataSchema>;

// ── Activity tiers (recurrence-based adoption) ───────────────────────────────
//
// Replaces the old "used at least once, ever" reading of network_adoption_pct
// with a cadence measure: what share of a team's/device's own possible
// sub-periods (week/month/quarter, scaled to the window) actually saw a
// qualifying session. See packages/backend/src/services/activity-tiers.ts.

export const ActivityTierSchema = z.enum(["highly_active", "active", "intermittent", "inactive"]);
export type ActivityTier = z.infer<typeof ActivityTierSchema>;

export const ActivityTierCountsSchema = z.object({
  highly_active: z.number().int().nonnegative(),
  active: z.number().int().nonnegative(),
  intermittent: z.number().int().nonnegative(),
  inactive: z.number().int().nonnegative(),
});

export type ActivityTierCounts = z.infer<typeof ActivityTierCountsSchema>;

export const ActivityTierResultSchema = z.object({
  key: z.string(),
  nActiveSubperiods: z.number().int().nonnegative(),
  nPossible: z.number().int().nonnegative(),
  fraction: z.number(),
  // null = insufficient history to classify.
  tier: ActivityTierSchema.nullable(),
});

export type ActivityTierResult = z.infer<typeof ActivityTierResultSchema>;

export const DeviceTierResultSchema = ActivityTierResultSchema.extend({
  // The team this device most recently belonged to.
  teamName: z.string(),
});

export type DeviceTierResult = z.infer<typeof DeviceTierResultSchema>;

export const ActivityTierBreakdownSchema = z.object({
  subPeriodUnit: z.enum(["week", "month", "quarter"]),
  totalSubPeriods: z.number().int().nonnegative(),
  teams: z.array(ActivityTierResultSchema),
  teamCounts: ActivityTierCountsSchema,
  // Share of tiered teams (excluding insufficient-history) at Active-or-better —
  // the new definition of "Network Adoption %".
  activeOrBetterPct: z.number(),
  devices: z.array(DeviceTierResultSchema),
  deviceCounts: ActivityTierCountsSchema,
  insufficientHistoryDeviceCount: z.number().int().nonnegative(),
  zeroUsageEverDeviceCount: z.number().int().nonnegative(),
});

export type ActivityTierBreakdown = z.infer<typeof ActivityTierBreakdownSchema>;

// Defaults to an empty breakdown so periods stored before this field existed
// still parse — their dashboardData JSON has no activity_tiers key.
const EMPTY_ACTIVITY_TIER_BREAKDOWN: ActivityTierBreakdown = {
  subPeriodUnit: "week",
  totalSubPeriods: 0,
  teams: [],
  teamCounts: { highly_active: 0, active: 0, intermittent: 0, inactive: 0 },
  activeOrBetterPct: 0,
  devices: [],
  deviceCounts: { highly_active: 0, active: 0, intermittent: 0, inactive: 0 },
  insufficientHistoryDeviceCount: 0,
  zeroUsageEverDeviceCount: 0,
};

export const DashboardDataSchema = z.object({
  methodology: MethodologyResultSchema,
  emergency: EmergencyDataSchema.nullable(),
  checkin: CheckinDataSchema.nullable(),
  team_alert: TeamAlertDataSchema.nullable(),
  activity_tiers: ActivityTierBreakdownSchema.default(EMPTY_ACTIVITY_TIER_BREAKDOWN),
});

export type DashboardData = z.infer<typeof DashboardDataSchema>;

export const PeriodDetailSchema = ReportingPeriodSchema.extend({
  dashboardData: DashboardDataSchema,
});

export type PeriodDetail = z.infer<typeof PeriodDetailSchema>;

// A feature-only upload (no usage CSV) is still processed synchronously and
// returns this shape directly (HTTP 201).
export const UploadPeriodResponseSchema = z.object({
  periods: z.array(ReportingPeriodSchema),
  created: z.number().int().nonnegative(),
  updated: z.number().int().nonnegative(),
});

export type UploadPeriodResponse = z.infer<typeof UploadPeriodResponseSchema>;

// A usage CSV/XLSX upload is partitioned by month and processed one month
// at a time via a Cloudflare Queue instead of in the upload request itself
// (see the backend's PeriodService.partitionUsageUpload) — this is what the
// upload endpoint returns instead (HTTP 202) whenever a usage file is
// present, and what the job-status endpoint below returns while polling.
export const AsyncUploadResponseSchema = z.object({
  jobId: z.string().uuid(),
  totalMonths: z.number().int().positive(),
});

export type AsyncUploadResponse = z.infer<typeof AsyncUploadResponseSchema>;

// The union a caller of the upload endpoint actually gets back — discriminate
// on `"jobId" in response`, not a status code, since the client only sees
// the parsed body.
export const UploadOutcomeSchema = z.union([UploadPeriodResponseSchema, AsyncUploadResponseSchema]);

export type UploadOutcome = z.infer<typeof UploadOutcomeSchema>;

export const UploadJobStatusSchema = z.object({
  status: z.enum(["partitioning", "processing", "completed", "failed"]),
  totalMonths: z.number().int().positive(),
  completedMonths: z.number().int().nonnegative(),
  currentMonth: z.object({ year: z.number().int(), month: z.number().int() }).nullable(),
  errorMessage: z.string().nullable(),
  stalled: z.boolean(),
  // Chronological order — the last element is the most recently-processed
  // month, i.e. the period the upload dialog should switch to once the job
  // completes.
  completedPeriodIds: z.array(z.string().uuid()),
});

export type UploadJobStatus = z.infer<typeof UploadJobStatusSchema>;

// Whether this client currently has an in-flight (partitioning/processing)
// usage upload — polled independently of any particular UploadPeriodDialog
// instance, so a job survives a dialog close/page refresh instead of
// becoming untrackable the moment nothing is polling its jobId anymore.
export const ActiveUploadJobSchema = z.object({
  jobId: z.string().uuid().nullable(),
});

export type ActiveUploadJob = z.infer<typeof ActiveUploadJobSchema>;

export const ComputedDashboardResponseSchema = z.object({
  from: z.string().nullable(),
  to: z.string().nullable(),
  sessionsTotal: z.number().int().nonnegative(),
  microSessionsExcluded: z.number().int().nonnegative(),
  dashboardData: DashboardDataSchema,
});

export type ComputedDashboardResponse = z.infer<typeof ComputedDashboardResponseSchema>;

export const PeriodTrendPointSchema = z.object({
  periodId: z.string().uuid(),
  year: z.number().int(),
  month: z.number().int(),
  label: z.string(),
  extended_session_rate: z.number(),
  productive_hours: z.number(),
  total_hours: z.number(),
  fleet_idle_rate_pct: z.number(),
  total_sessions: z.number(),
  active_stores: z.number(),
  total_stores: z.number(),
  // Recurrence-based adoption (activity_tiers.activeOrBetterPct) — the same
  // definition the live Executive Summary tab shows, not the older
  // roster-ratio kpis.network_adoption_pct.
  network_adoption_pct: z.number(),
});

export type PeriodTrendPoint = z.infer<typeof PeriodTrendPointSchema>;

export const PeriodTrendsResponseSchema = z.object({
  items: z.array(PeriodTrendPointSchema),
});

export type PeriodTrendsResponse = z.infer<typeof PeriodTrendsResponseSchema>;

// ── Daily team activity (L7 / L28) ───────────────────────────────────────────
//
// Recency/recurrence view, always bucketed by calendar day within a single
// selected reporting period (month) — not a live rolling window. See
// project-management/requirements for the underlying methodology.

export const DailyActivityTierSchema = z.enum(["highly_active", "occasional", "rare", "dark"]);
export type DailyActivityTier = z.infer<typeof DailyActivityTierSchema>;

export const DailyActivityDaySchema = z.object({
  date: z.string(),
  active: z.boolean(),
});

export type DailyActivityDay = z.infer<typeof DailyActivityDaySchema>;

export const TeamDailyActivitySchema = z.object({
  store: z.string(),
  devicesTotal: z.number().int().nonnegative(),
  days: z.array(DailyActivityDaySchema),
  l7: z.number().int().nonnegative(),
  l28: z.number().int().nonnegative(),
  tier: DailyActivityTierSchema,
});

export type TeamDailyActivity = z.infer<typeof TeamDailyActivitySchema>;

export const DailyActivityResponseSchema = z.object({
  periodId: z.string().uuid(),
  year: z.number().int().min(2000).max(2100),
  month: z.number().int().min(1).max(12),
  teams: z.array(TeamDailyActivitySchema),
});

export type DailyActivityResponse = z.infer<typeof DailyActivityResponseSchema>;

// ── Team device breakdown ────────────────────────────────────────────────────
//
// Support staff drill-down: every distinct device seen for one team within one
// reporting period, straight off the raw session rows -- deliberately
// unfiltered by the micro-session threshold that methodology totals exclude,
// since the point of this view is to show exactly what was recorded, not the
// already-aggregated numbers Team Performance shows.

export const TeamDeviceBreakdownItemSchema = z.object({
  deviceKey: z.string(),
  deviceId: z.string(),
  watchName: z.string(),
  deviceType: z.string(),
  sessions: z.number().int().nonnegative(),
  totalHours: z.number().nonnegative(),
  lastSessionDate: z.string(),
});

export type TeamDeviceBreakdownItem = z.infer<typeof TeamDeviceBreakdownItemSchema>;

export const TeamDeviceBreakdownResponseSchema = z.object({
  periodId: z.string().uuid(),
  team: z.string(),
  devices: z.array(TeamDeviceBreakdownItemSchema),
});

export type TeamDeviceBreakdownResponse = z.infer<typeof TeamDeviceBreakdownResponseSchema>;

const dateParamSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Must be in YYYY-MM-DD format")
  .optional();

export const ExportQuerySchema = z.object({
  period: z.string().uuid("Must be a valid period ID").optional(),
  from: dateParamSchema,
  to: dateParamSchema,
});

export type ExportQuery = z.infer<typeof ExportQuerySchema>;
